/**
 * ImportDialog — CSV/XLSX import wizard for runtime list views.
 *
 * Steps:
 *   1. Upload file → preview columns + sample data
 *   2. Map file columns → object fields
 *   3. Execute import → show results
 */
import React, { useState, useCallback, useEffect } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Chip from '@mui/material/Chip'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import apiClient from '@/utils/api'
import { ApiService } from '@/utils/api'
import { RESIZABLE_DIALOG_PAPER } from '@/shared/resizableDialog'

interface ImportDialogProps {
  open: boolean
  onClose: () => void
  connectionId: string
  objectName: string
  onImported?: () => void
  initialFile?: File | null
}

interface ObjectField {
  name: string
  title: string
  type: string
  required: boolean
  unique?: boolean
}

const STEPS = ['Upload file', 'Map columns', 'Import']

export function ImportDialog({ open, onClose, connectionId, objectName, onImported, initialFile }: ImportDialogProps) {
  const [step, setStep] = useState(0)
  const [file, setFile] = useState<File | null>(null)
  const [fileBytes, setFileBytes] = useState<ArrayBuffer | null>(null)
  const [preview, setPreview] = useState<{ columns: string[]; preview: Record<string, any>[]; total_rows: number } | null>(null)
  const [fields, setFields] = useState<ObjectField[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [matchKey, setMatchKey] = useState<string>('')
  const [skipErrors, setSkipErrors] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ imported: number; created?: number; updated?: number; errors: any[]; total_rows: number } | null>(null)

  const reset = () => {
    setStep(0)
    setFile(null)
    setFileBytes(null)
    setPreview(null)
    setMapping({})
    setMatchKey('')
    setError(null)
    setResult(null)
  }

  const handleFileSelect = useCallback(async (f: File) => {
    setFile(f)
    setError(null)
    setLoading(true)
    try {
      const bytes = await f.arrayBuffer()
      setFileBytes(bytes)
      // Upload for preview
      const formData = new FormData()
      formData.append('file', f)
      const resp = await apiClient.post(
        `/v1/import/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/preview`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      setPreview(resp.data)

      // Get object fields
      const fieldsResp = await ApiService.get<{ fields: ObjectField[] }>(
        `/v1/import/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/fields`,
      )
      setFields(fieldsResp.fields)

      // Auto-map by name match
      const autoMap: Record<string, string> = {}
      for (const col of resp.data.columns) {
        const colLower = col.toLowerCase().trim()
        const match = fieldsResp.fields.find(
          (f: ObjectField) => f.name.toLowerCase() === colLower || f.title.toLowerCase() === colLower,
        )
        if (match) autoMap[col] = match.name
      }
      setMapping(autoMap)

      // Set default match key: code if present, else first unique string field, else first field
      const codeField = fieldsResp.fields.find((f: ObjectField) => f.name === 'code')
      const firstUniqueStringField = fieldsResp.fields.find((f: ObjectField) => f.unique && f.type === 'string')
      const defaultMatchKey = codeField ? 'code' : firstUniqueStringField ? firstUniqueStringField.name : fieldsResp.fields[0]?.name || ''
      setMatchKey(defaultMatchKey)

      setStep(1)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to parse file')
    }
    setLoading(false)
  }, [connectionId, objectName])

  // A mounted dialog can be reopened with a different initialFile — clear the
  // previous session's wizard state or a stale file could be imported.
  useEffect(() => {
    if (open) reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // When opened with a pre-picked file (unified list-view entry), skip the picker
  useEffect(() => {
    if (open && initialFile && step === 0 && !file && !loading) {
      handleFileSelect(initialFile)
    }
  }, [open, initialFile, step, file, loading, handleFileSelect])

  const handleExecute = useCallback(async () => {
    if (!file || !fileBytes) return
    setLoading(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const params = new URLSearchParams({
        column_mapping: JSON.stringify(mapping),
        skip_errors: skipErrors.toString(),
        ...(matchKey && { match_key: matchKey })
      })
      const resp = await apiClient.post(
        `/v1/import/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/execute?${params}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      setResult(resp.data)
      setStep(2)
      if (resp.data.imported > 0) onImported?.()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Import failed')
    }
    setLoading(false)
  }, [file, fileBytes, connectionId, objectName, mapping, matchKey, skipErrors, onImported])

  const mappedCount = Object.values(mapping).filter(Boolean).length
  
  // Check if parent_id field exists to show parent_code option
  const hasParentIdField = fields.some(f => f.name === 'parent_id')

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: RESIZABLE_DIALOG_PAPER }}
    >
      <DialogTitle>Import data — {objectName}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Step 0: Upload */}
        {step === 0 && (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => document.getElementById('import-file-input')?.click()}
          >
            <input
              id="import-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Click to select a CSV or Excel file
            </Typography>
            <Typography variant="caption" color="text.disabled">
              .csv, .xlsx, .xls
            </Typography>
            {loading && <CircularProgress size={24} sx={{ mt: 2 }} />}
          </Box>
        )}

        {/* Step 1: Map columns */}
        {step === 1 && preview && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {preview.total_rows} rows found. Map file columns to object fields:
            </Typography>
            
            {/* Match key selector */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Match key for updates:
                </Typography>
                <Select
                  value={matchKey}
                  onChange={(e) => setMatchKey(e.target.value)}
                  displayEmpty
                  data-testid="match-key-select"
                >
                  <MenuItem value="">
                    <em>— none (insert only) —</em>
                  </MenuItem>
                  {fields.map((f) => (
                    <MenuItem key={f.name} value={f.name}>
                      {f.title} {f.unique && <Chip label="unique" size="small" sx={{ ml: 0.5, height: 16, fontSize: 9 }} />}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TableContainer sx={{ maxHeight: 400, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>File column</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sample</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 200 }}>Map to field</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.columns.map((col) => (
                    <TableRow key={col}>
                      <TableCell>{col}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {preview.preview[0]?.[col] ?? '—'}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={mapping[col] || ''}
                            onChange={(e) => setMapping((prev) => ({ ...prev, [col]: e.target.value }))}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>— skip —</em>
                            </MenuItem>
                            {hasParentIdField && (
                              <MenuItem value="parent_code">
                                Parent code (hierarchy)
                              </MenuItem>
                            )}
                            {fields.map((f) => (
                              <MenuItem key={f.name} value={f.name}>
                                {f.title} {f.required && <Chip label="req" size="small" sx={{ ml: 0.5, height: 16, fontSize: 9 }} />}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <FormControlLabel
              control={<Checkbox size="small" checked={skipErrors} onChange={(_, v) => setSkipErrors(v)} />}
              label="Skip rows with errors (continue importing)"
            />
          </>
        )}

        {/* Step 2: Results */}
        {step === 2 && result && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" color={result.imported > 0 ? 'success.main' : 'error.main'} sx={{ mb: 1 }}>
              {result.imported} / {result.total_rows}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              records imported successfully
            </Typography>
            
            {/* Show Created/Updated breakdown if available */}
            {(result.created !== undefined || result.updated !== undefined) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                {result.created !== undefined && (
                  <Chip 
                    label={`Created: ${result.created}`}
                    color="success" 
                    variant="outlined"
                    size="small"
                  />
                )}
                {result.updated !== undefined && (
                  <Chip 
                    label={`Updated: ${result.updated}`}
                    color="info" 
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            )}
            {result.errors.length > 0 && (
              <Alert severity="warning" sx={{ textAlign: 'left', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{result.errors.length} error(s):</Typography>
                {result.errors.slice(0, 5).map((err, i) => (
                  <Typography key={i} variant="caption" display="block">
                    Row {err.row}: {err.error}
                  </Typography>
                ))}
                {result.errors.length > 5 && (
                  <Typography variant="caption" color="text.secondary">
                    ...and {result.errors.length - 5} more
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 2 ? (
          <>
            <Button onClick={() => { reset(); }}>Import another</Button>
            <Button variant="contained" onClick={onClose}>Done</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            {step === 1 && (
              <Button
                variant="contained"
                onClick={handleExecute}
                disabled={loading || mappedCount === 0}
              >
                {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Import {preview?.total_rows ?? 0} rows ({mappedCount} fields mapped)
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
